import { useEffect, useRef, useState, useCallback } from 'react';
import { Input } from './Input';
import { KeyHint } from './KeyHint';
import { Row } from './Row';
import { Text } from './Text';
import { useNavigate } from 'react-router-dom';

type Cmd = { id: string; label: string; hint?: string; run: () => void };

export function CommandBar() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const nav = useNavigate();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(o => !o);
      } else if (e.key === 'Escape') {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => { if (open) setTimeout(() => inputRef.current?.focus(), 10); }, [open]);

  const cmds: Cmd[] = [
    { id: 'p', label: 'Go to Pulse', hint: 'g p', run: () => { nav('/'); setOpen(false); } },
    { id: 'f', label: 'Go to People', hint: 'g f', run: () => { nav('/people'); setOpen(false); } },
    { id: 'd', label: 'Go to Deals', hint: 'g d', run: () => { nav('/deals'); setOpen(false); } },
    { id: 'h', label: 'Go to Hangs', hint: 'g h', run: () => { nav('/hangs'); setOpen(false); } },
    { id: 'v', label: 'Go to Vault', hint: 'g v', run: () => { nav('/vault'); setOpen(false); } },
    { id: 'nd', label: 'New deal', hint: 'n d', run: () => { nav('/deals/new'); setOpen(false); } },
    { id: 'ni', label: 'Log intro request', hint: 'n i', run: () => { nav('/people'); setOpen(false); } },
    { id: 'nb', label: 'Toggle travel beacon', hint: 'n b', run: () => { setOpen(false); } },
    { id: 'na', label: 'Ask the agent...', hint: '?', run: () => { setOpen(false); } },
  ];
  const filtered = q ? cmds.filter(c => c.label.toLowerCase().includes(q.toLowerCase())) : cmds;

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center sm:pt-[12vh] bg-ink/30" onClick={() => setOpen(false)}>
      <div className="w-full sm:w-[560px] sm:max-w-[92vw] bg-bg hairline sm:rounded-sm shadow-none" onClick={e => e.stopPropagation()}>
        <div className="hairline-b px-2 py-2">
          <Input ref={inputRef} value={q} onChange={e => setQ(e.target.value)} placeholder="type a command, or ask the agent…" />
        </div>
        <div className="max-h-80 overflow-auto">
          {filtered.length === 0 && (
            <div className="px-3 py-3 text-sm text-muted">no matches — press enter to ask the agent</div>
          )}
          {filtered.map(c => (
            <Row key={c.id} justify="between" className="px-3 py-2 hover:bg-whisper cursor-pointer" align="center" >
              <div onClick={c.run} className="flex-1"><Text variant="small">{c.label}</Text></div>
              {c.hint && <KeyHint>{c.hint}</KeyHint>}
            </Row>
          ))}
        </div>
      </div>
    </div>
  );
}
